"use client";

import { useEffect } from "react";

import { createInsightScrollMilestoneSession } from "@/lib/insight-analytics";
import { captureMarketingEvent } from "@/lib/marketing-analytics-client";

type InsightScrollTrackerProps = {
  articleBodyId: string;
  insightSlug: string;
};

type MeasurementTrigger = "initial" | "resize" | "scroll";

const triggerPriority: Record<MeasurementTrigger, number> = {
  initial: 0,
  resize: 1,
  scroll: 2,
};

const scrollKeys = new Set([
  " ",
  "ArrowDown",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

export function InsightScrollTracker({
  articleBodyId,
  insightSlug,
}: InsightScrollTrackerProps) {
  useEffect(() => {
    const articleBody = document.getElementById(articleBodyId);

    if (!articleBody) {
      return;
    }

    const session = createInsightScrollMilestoneSession({
      insightSlug,
      capture(eventName, properties) {
        captureMarketingEvent(eventName, properties, {
          analyticsAudience: "public_only",
        });
      },
    });
    let animationFrameId: number | null = null;
    let pendingTrigger: MeasurementTrigger = "initial";
    let hasCompletedInitialMeasurement = false;
    let hasScrollIntent = false;

    const measure = () => {
      animationFrameId = null;
      const rect = articleBody.getBoundingClientRect();
      const documentScrollTop = window.scrollY;

      session.record({
        articleTop: documentScrollTop + rect.top,
        articleHeight: rect.height,
        viewportBottom: documentScrollTop + window.innerHeight,
        trigger: hasCompletedInitialMeasurement ? pendingTrigger : "initial",
      });
      hasCompletedInitialMeasurement = true;
      pendingTrigger = "initial";
    };

    const scheduleMeasurement = (trigger: MeasurementTrigger) => {
      if (triggerPriority[trigger] > triggerPriority[pendingTrigger]) {
        pendingTrigger = trigger;
      }

      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(measure);
      }
    };

    const markScrollIntent = () => {
      hasScrollIntent = true;
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (scrollKeys.has(event.key)) {
        markScrollIntent();
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      const scrollbarEdge = document.documentElement.clientWidth - 24;

      if (event.clientX >= scrollbarEdge) {
        markScrollIntent();
      }
    };
    const handleScroll = () => {
      if (hasScrollIntent) {
        scheduleMeasurement("scroll");
      }
    };
    const handleResize = () => {
      scheduleMeasurement("resize");
    };

    window.addEventListener("wheel", markScrollIntent, { passive: true });
    window.addEventListener("touchmove", markScrollIntent, { passive: true });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointerDown, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(handleResize);
    resizeObserver?.observe(articleBody);
    scheduleMeasurement("initial");

    return () => {
      window.removeEventListener("wheel", markScrollIntent);
      window.removeEventListener("touchmove", markScrollIntent);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [articleBodyId, insightSlug]);

  return null;
}
